import CustomButton from '@/common/CustomButton';
import {
  CalendarOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined
} from '@ant-design/icons';
import { useCallback, useEffect, useState } from 'react';
import CreateFollowUpModal from './CreateFollowUpModal';
import CustomLayout from '@/common/Layout';
import { EXECUTIVE, MANAGER } from 'utilities/roleRights';
import FloatingActionButton from 'pages/FloatingActionButton';
import {
  columnRenderer,
  FOLLOW_UP_STATUS_TYPES,
  getFormattedDateToDisplay,
  getLoggedInUser,
  headerRenderer
} from 'utilities/helpers';
import { Input, Popconfirm, Tag, Tooltip } from 'antd';
import Table from './Table';
import MarkAsCompleteModal from './MarkAsCompleteModal';
import RescheduleModal from './RescheduleModal';
import { useDispatch } from 'react-redux';
import { createFollowUp, getFollowUps } from '../redux/slice';
import { debounce } from 'lodash';

const options = ['All', 'My Follow-ups'];

const RECORDS_PER_PAGE = 20;

export default function FollowUpTablePage() {
  const user = getLoggedInUser();
  const [isNewFollowUpModalOpen, setIsNewFollowUpModalOpen] = useState(false);
  const [isMarkAsCompleteModalOpen, setIsMarkAsCompleteModalOpen] =
    useState(null);
  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(null);
  const [selected, setSelected] = useState(options[0]);
  const [followUpData, setFollowUpData] = useState({
    pagination: {},
    data: []
  });
  const dispatch = useDispatch();

  const toggleOption = (option) => {
    setSelected(option);
  };

  const handleLoadFollowUps = (
    search = '',
    page = 1,
    per_page = RECORDS_PER_PAGE
  ) => {
    const payload = {
      search,
      type: selected === 'All' ? 'team' : 'user_list',
      page,
      per_page
    };
    dispatch(getFollowUps(payload)).then((res) => {
      if (res?.payload?.success) {
        setFollowUpData({
          pagination: {
            current: res?.payload?.pagination.current_page,
            pageSize: RECORDS_PER_PAGE,
            total: res?.payload?.pagination.total_pages
          },
          data: res?.payload?.data
        });
      }
    });
  };

  useEffect(() => {
    handleLoadFollowUps();
  }, [selected]);

  const handleFollowUpDelete = (id) => {
    const payload = {
      type: 'delete',
      id
    };

    dispatch(createFollowUp(payload)).then((res) => {
      if (res?.payload?.success) {
        handleLoadFollowUps('', followUpData.pagination.current);
      }
    });
  };

  const columns = [
    {
      title: headerRenderer('Follow up on'),
      dataIndex: 'title',
      key: 'title',
      width: '20%',
      render: (text) => columnRenderer(text)
    },
    {
      title: headerRenderer('With'),
      dataIndex: 'with_whom',
      key: 'with_whom',
      width: '15%',
      render: (text) => columnRenderer(text, '!text-gray-500')
    },
    {
      title: headerRenderer('Assigned To'),
      dataIndex: 'assigned_to',
      key: 'assigned_to',
      width: '15%',
      render: (text) => columnRenderer(text)
    },
    {
      title: headerRenderer('Created By'),
      dataIndex: 'created_by',
      key: 'created_by',
      width: '15%',
      render: (text) => columnRenderer(text)
    },
    {
      title: headerRenderer('Due Date'),
      dataIndex: 'due_date',
      key: 'due_date',
      width: '15%',
      render: (text) => columnRenderer(text, '!text-gray-500')
    },
    {
      title: headerRenderer('Status'),
      dataIndex: 'status',
      key: 'status',
      width: '10%',
      render: (text) => {
        return (
          <div
            className={
              text === FOLLOW_UP_STATUS_TYPES.overdue
                ? 'invisible'
                : 'block !capitalize'
            }
          >
            {text === FOLLOW_UP_STATUS_TYPES.pending ? (
              <Tag color="warning" className="!text-xs">
                {text}
              </Tag>
            ) : text === FOLLOW_UP_STATUS_TYPES.completed ? (
              <Tag color="success" className="!text-xs">
                {text}
              </Tag>
            ) : (
              <Tag color="default" className="!text-xs">
                {text}
              </Tag>
            )}
          </div>
        );
        // const classes = `${
        //   text === FOLLOW_UP_STATUS_TYPES.overdue
        //     ? 'invisible'
        //     : text === FOLLOW_UP_STATUS_TYPES.completed
        //     ? ' text-[#52c41a]'
        //     : text === FOLLOW_UP_STATUS_TYPES.pending
        //     ? 'text-[#faad14]'
        //     : '!text-gray-500'
        // } !capitalize`;
        // return columnRenderer(text, classes);
      }
    },
    {
      title: headerRenderer('Activity'),
      dataIndex: 'activity',
      key: 'activity',
      width: '10%',
      render: (text, record) => (
        <div
          className={`${
            record.isCompleted ? 'invisible' : 'grid'
          } grid-cols-4 gap-4 place-items-center`}
        >
          <Tooltip title="Edit" overlayClassName="!text-xs">
            <EditOutlined
              onClick={() => setIsNewFollowUpModalOpen(record.id)}
              className="text-lg cursor-pointer text-[#0653fa] hover:!scale-105 "
            />
          </Tooltip>
          <Tooltip title="Reschedule" overlayClassName="!text-xs">
            <CalendarOutlined
              onClick={() => setIsRescheduleModalOpen(record.id)}
              className="text-lg cursor-pointer text-[#64748b] hover:!scale-105 "
            />
          </Tooltip>
          <Tooltip title="Mark As Done" overlayClassName="!text-xs">
            <CheckCircleOutlined
              onClick={() => setIsMarkAsCompleteModalOpen(record.id)}
              className="text-lg cursor-pointer text-[#52c41a] hover:!scale-105 "
            />
          </Tooltip>
          <Tooltip title="Delete" overlayClassName="!text-xs">
            <Popconfirm
              title={
                <>
                  <div>Please Confirm</div>
                  <div
                    style={{
                      color: 'rgba(0, 0, 0, 0.45)'
                    }}
                  >
                    Are you sure you want to delete this follow-up?
                  </div>
                </>
              }
              onConfirm={() => handleFollowUpDelete(record.id)}
              okText="Delete"
              cancelText="No"
              placement="left"
            >
              <DeleteOutlined className="text-lg cursor-pointer text-[#ef4444] hover:!scale-105 " />
            </Popconfirm>
          </Tooltip>
        </div>
      )
    }
  ];

  const dataSource = followUpData.data.map((task) => {
    return {
      title: task.title,
      assigned_to: task.assignee.first_name + ' ' + task.assignee.last_name,
      created_by: task.creator.first_name + ' ' + task.creator.last_name,
      with_whom: task.with_whom,
      due_date: getFormattedDateToDisplay(task.followup_date),
      task: task,
      status: task.status,
      isCompleted: task.status === FOLLOW_UP_STATUS_TYPES.completed,
      id: task.id
    };
  });

  const debouncedSearch = useCallback(
    debounce((value) => {
      handleLoadFollowUps(value);
    }, 300),
    []
  );

  const handleTableChange = (newPagination) => {
    handleLoadFollowUps('', newPagination.current, newPagination.pageSize);
  };

  return (
    <CustomLayout>
      <div className="flex flex-col gap-6">
        {/* header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            <h5
              className={
                '!text-xl md:!text-2xl lg:!text-3xl !font-bold md:mt-0 mt-2'
              }
            >
              All Follow-ups
            </h5>

            <div className="!hidden md:!flex !items-center gap-2">
              {options.map((option) => {
                const isSelected = selected === option;
                return (
                  <div
                    key={option}
                    style={{
                      fontFamily: 'SFProText Bold',
                      border: '1px solid #dddddd',
                      borderRadius: '4px',
                      paddingBlock: '4px',
                      paddingInline: '6px',
                      fontSize: '12px',
                      cursor: isSelected ? 'not-allowed' : 'pointer',
                      backgroundColor: isSelected ? '#0653fa' : 'white',
                      color: isSelected ? 'white' : 'black'
                    }}
                    onClick={() => toggleOption(option)}
                  >
                    {option}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="items-center hidden gap-4 md:flex">
            <Input
              placeholder="Search follow-ups"
              prefix={<SearchOutlined style={{ color: '#00000072' }} />}
              allowClear
              className="!w-52"
              onChange={(e) => debouncedSearch(e.target.value)}
            />
            <CustomButton
              icon={<PlusOutlined />}
              className={'!text-sm rounded-md'}
              type="primary"
              onClick={() => setIsNewFollowUpModalOpen(true)}
            >
              New Follow-up
            </CustomButton>
          </div>
          <CustomButton
            icon={<PlusOutlined />}
            className={'!text-sm rounded-md block md:hidden'}
            type="primary"
            size="small"
            onClick={() => setIsNewFollowUpModalOpen(true)}
          >
            New Follow-up
          </CustomButton>
        </div>

        {/* action buttons */}
        <div className="flex items-center justify-between gap-2 md:gap-3 md:hidden">
          <div className="!flex !items-center gap-1 md:gap-2">
            {options.map((option) => {
              const isSelected = selected === option;
              return (
                <div
                  key={option}
                  className="md:!text-xs !text-[10px]"
                  style={{
                    fontFamily: 'SFProText Bold',
                    border: '1px solid #dddddd',
                    borderRadius: '4px',
                    paddingBlock: '4px',
                    paddingInline: '6px',
                    cursor: isSelected ? 'not-allowed' : 'pointer',
                    backgroundColor: isSelected ? '#0653fa' : 'white',
                    color: isSelected ? 'white' : 'black'
                  }}
                  onClick={() => toggleOption(option)}
                >
                  {option}
                </div>
              );
            })}
          </div>
          <Input
            placeholder="Search follow-ups"
            prefix={<SearchOutlined style={{ color: '#00000072' }} />}
            allowClear
            className="!w-auto"
            onChange={(e) => debouncedSearch(e.target.value)}
          />
        </div>

        <Table
          columns={columns}
          pagination={followUpData?.pagination}
          loading={false}
          dataSource={dataSource}
          onChange={handleTableChange}
        />

        {/* new and create follow up modal */}
        <CreateFollowUpModal
          setIsOpen={setIsNewFollowUpModalOpen}
          isOpen={isNewFollowUpModalOpen}
          onAssign={handleLoadFollowUps}
          data={
            typeof isNewFollowUpModalOpen === 'number'
              ? followUpData.data?.find(
                  (task) => task.id === isNewFollowUpModalOpen
                )
              : null
          }
        />

        {/*mark as complete modal */}
        <MarkAsCompleteModal
          setIsOpen={setIsMarkAsCompleteModalOpen}
          isOpen={isMarkAsCompleteModalOpen}
          onAssign={handleLoadFollowUps}
          data={followUpData.data?.find(
            (task) => task.id === isMarkAsCompleteModalOpen
          )}
        />

        {/*reschedule modal */}
        <RescheduleModal
          setIsOpen={setIsRescheduleModalOpen}
          isOpen={isRescheduleModalOpen}
          onAssign={handleLoadFollowUps}
          data={followUpData.data?.find(
            (task) => task.id === isRescheduleModalOpen
          )}
        />
      </div>
      {[MANAGER, EXECUTIVE].includes(user?.user_details?.role_id) ? (
        <FloatingActionButton />
      ) : (
        ''
      )}
    </CustomLayout>
  );
}
